<template>
<div class="chapter">
<div class="inner_page">
<p class="h1" id="pz9-1">
<a href="#/Contents#a2a" id="b2a">Introduction </a></p>
<p class="tx" id="pz9-2">
					How many times each day do you exchange information with others, either in writing
					or by speaking? How much communication takes place at work? How much takes place
					at home or with friends? </p>
<p class="tx" id="Ab9">
					Most people have few problems with informal verbal exchanges, but in formal situations
					they often grow nervous and unsure of their communication skills. What about work
					situations? Even if your situation at work is not strictly “formal,” communication
					with customers or clients, coworkers, bosses, employees, service people and a whole
					host of others needs to be accurate and concise. Careless speech or writing results
					in misunderstandings or confusion, and sadly often creates hurt feelings and even
					anger. </p>
<p class="tx" id="pz9-3">
					So, just how important <i>are</i> effective communication skills? The short answer is <i>critically
					important.</i> As an instructor of business communications at the university level, I
					hear the question – and skepticism – about the importance of effective communication
					skills every semester. Many students, as well as some business professionals, express
					doubt about the need for good communication skills. Often they believe that whatever
					<span id="z10-1">communication skills they already possess will serve them well enough. Perhaps that
					is true if the person’s skills <i>are</i> good, but not so much if the person’s skills are
					less so. Having learned how to read and write in grade school and high school does
					not necessarily mean one has nothing left to learn or upon which to improve. </span></p>
<p class="tx" id="pz10-2">
					Employer surveys consistently reveal the critical nature of effective communication
					skills for securing a job, job performance, career advancement and success, as well
					as for the success of a business or organization. Managers are leaders. Leaders need
					good communication skills to convey information accurately to others for the effective
					operation of the business. Take a few minutes to check job postings for professional
					positions (online or hard-copy newspapers, job posting Web sites, company Web sites,
					etc.). Do you find that most of them require good communications skills? The answer
					most likely is “yes.” The one competency consistently required for positions in a
					wide variety of businesses and jobs <i>is</i> those good communication skills, both written
					and verbal. Many postings specify <i>excellent</i>, not only good, communication skills.
					</p>
<p class="tx" id="pz10-3">
					Good communication reflects directly on the company. Spelling and grammar <i>DO</i> count.
					Have you ever received a marketing letter so poorly written that you simply tossed
					it in the trash? Most of us have had that experience. You don’t want your communications
					to be the ones going in the trash. You don’t need – or <span id="z11-1">want – to sound like someone
					whose favorite pastime is flaunting a huge vocabulary with long mysterious words,
					but you do want to sound as if you at least remember your junior high grammar and
					composition lessons, as well as your high school speech class lessons. Using simple
					words that everyone understands is preferable to using big words that few people
					know. Just make sure that whatever words you do use are used correctly. Strive to
					reflect your company in the best way possible. </span></p>
<p class="tx" id="pz11-2">
					Effective communication is essential for any business. Business owners and managers
					actively seek employees who express ideas and information clearly and accurately
					to customers, clients, coworkers, managers, shareholders, the community, etc. Clear,
					effective communication creates trust among staff and customers, presents a professional
					image of the company and builds better business relationships – all contributing
					to a more successful business. All people in business – employees, managers, CEOs,
					owners – possess an advantage with good communication skills. </p>
<p class="tx" id="pz11-3">
					The exchange of information makes up a significant portion of a business’s activity.
					That information must be accurate, as well as easily and clearly understood by the
					reader (or listener ). Easily and clearly mean keeping the writing <i>SIMPLE</i>. One of
					my “rules of thumb” (or perhaps “dictates” may be more accurate) is that the writer’s
					job is to make sure the reader understands the material <i>clearly</i> the <i>first</i> time he
					or she <span id="z12-1">reads it. Avoid making the reader go back to reread. Do you remember some
					of those textbooks that made us students reread each paragraph three, four, five
					times or more to understand what the writer said? We’ve all encountered those beasts
					at one time or another. When you are writing, always keep that frustrating experience
					in mind. Be kind to your readers. Make reading what you’ve written easy for your
					readers to understand. Keep it clear. Keep it simple. Keep it to the point. </span></p>
<p class="tx" id="pz12-2">
					Before anyone panics, remember that good communications skills are <i>learned</i>. No one
					is born an excellent communicator. Let me repeat: <b><i>Effective communication is a learned
					skill</i>. Anyone can learn the skills to communicate effectively in both written and
					verbal messages.</b> I learned. That fact means that <i>you</i> can do it, too. This book provides
					basic information to help you improve both written and verbal communication skills.
					</p>
<p class="tx" id="pz12-3">
<i>Rule of Thumb: A Guide to Communication Basics for Business Owners &amp; Managers</i>
					is part of the <i>Rule of Thumb</i> series of books for small businesses. This book offers
					basic information in plain language that will help you improve your business communication
					skills to enhance your ability to operate your business more effectively. </p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// Introduction.vue?2f7fa574